import { useState } from 'react';
import Button from './Button';
import OfficerDidSelect from './OfficerDidSelect';
import { OFFICE_COUNTRIES } from '../data/officeCountries';
import { useApprovedOfficers } from '../hooks/useApprovedOfficers';
import { parseOfficerDid } from '../web3/applicationEnvelope';
import { persistAndSubmitApplication } from '../web3/persistApplication';
import { ensureSessionProof, notifyApplicationSubmitted } from '../web3/serverApi';
import type { SessionProof } from '../web3/serverContract';
import { sealApplicationForInsert } from '../web3/sealApplication';
import { useWeb3Auth } from '../web3/Web3AuthContext';
import GetDidCta from './GetDidCta';
import {
  applicationSubmitError,
  submitErrorNeedsDidOnboarding,
} from '../web3/didEligibility';

type FormData = {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  otherCountry: string;
  achievements: string;
  goals: string;
  pastExperiences: string;
  sportsOfficer: string;
};

const SportsAgentForm = () => {
  const web3 = useWeb3Auth();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    otherCountry: '',
    achievements: '',
    goals: '',
    pastExperiences: '',
    sportsOfficer: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { officers, loading } = useApprovedOfficers(formData.country, web3.chainId);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (formData.country === 'Other' && !formData.otherCountry) {
      newErrors.otherCountry = 'Please specify your country';
    }
    if (!formData.achievements) newErrors.achievements = 'Achievements are required';
    if (!formData.goals) newErrors.goals = 'Goals are required';
    if (!formData.pastExperiences) newErrors.pastExperiences = 'Past experiences are required';
    if (!formData.sportsOfficer) newErrors.sportsOfficer = 'Sports Officer selection is required';
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'country') {
        return {
          ...prev,
          [name]: value,
          sportsOfficer: value === 'Other' ? 'Needs Review' : '',
          otherCountry: value === 'Other' ? prev.otherCountry : ''
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const sendTelegramNotification = async (didId: number, session: SessionProof) => {
    try {
      const { sent } = await notifyApplicationSubmitted({ role: 'agent', didId, session });
      if (!sent) console.error('Telegram bot configuration missing');
    } catch (error) {
      console.error('Failed to send Telegram notification:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitting(true);
      try {
        const countryValue = formData.country === 'Other' ? formData.otherCountry : formData.country;
        const sealed = await sealApplicationForInsert({
          role: 'agent',
          connectedAddress: web3.address,
          plaintext: {
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            country: countryValue,
            achievements: formData.achievements,
            goals: formData.goals,
            past_experiences: formData.pastExperiences,
            sports_officer_did: parseOfficerDid(formData.sportsOfficer),
          },
        });

        const raw = web3.getRawProvider();
        if (!raw || !web3.chainId) {
          throw new Error('Connect your wallet on Apertum to submit on-chain.');
        }
        const session = await ensureSessionProof(web3.signIn);
        await persistAndSubmitApplication({
          sealed,
          role: 'agent',
          session,
          rawProvider: raw,
          chainId: web3.chainId,
          publicMeta: {
            country: countryValue,
            officerDid: parseOfficerDid(formData.sportsOfficer),
          },
        });

        await sendTelegramNotification(sealed.did_id, session);
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors(prev => ({
          ...prev,
          submit: applicationSubmitError(error),
        }));
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <h3 className="text-2xl font-semibold text-green-800 mb-4">
          Thank you for your application!
        </h3>
        <p className="text-green-700 mb-6">
          We will review your application and get back to you soon.
        </p>
        <Button
          variant="secondary"
          onClick={() => setSubmitted(false)}
        >
          Submit Another Application
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.fullName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary border-gray-300"
          />
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select country</option>
            {OFFICE_COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
        </div>

        {formData.country === 'Other' && (
          <div>
            <label htmlFor="otherCountry" className="block text-sm font-medium text-gray-700 mb-1">
              Specify Country <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="otherCountry"
              name="otherCountry"
              value={formData.otherCountry}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.otherCountry ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your country"
            />
            {errors.otherCountry && <p className="mt-1 text-sm text-red-500">{errors.otherCountry}</p>}
          </div>
        )}
      </div>

      <OfficerDidSelect
        country={formData.country}
        value={formData.sportsOfficer}
        officers={officers}
        loading={loading}
        error={errors.sportsOfficer}
        onChange={handleChange}
      />

      <div>
        <label htmlFor="achievements" className="block text-sm font-medium text-gray-700 mb-1">
          Achievements <span className="text-red-500">*</span>
        </label>
        <textarea
          id="achievements"
          name="achievements"
          value={formData.achievements}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.achievements ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.achievements && <p className="mt-1 text-sm text-red-500">{errors.achievements}</p>}
      </div>

      <div>
        <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-1">
          Goals <span className="text-red-500">*</span>
        </label>
        <textarea
          id="goals"
          name="goals"
          value={formData.goals}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.goals ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.goals && <p className="mt-1 text-sm text-red-500">{errors.goals}</p>}
      </div>

      <div>
        <label htmlFor="pastExperiences" className="block text-sm font-medium text-gray-700 mb-1">
          Past Experiences <span className="text-red-500">*</span>
        </label>
        <textarea
          id="pastExperiences"
          name="pastExperiences"
          value={formData.pastExperiences}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.pastExperiences ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.pastExperiences && <p className="mt-1 text-sm text-red-500">{errors.pastExperiences}</p>}
      </div>

      {errors.submit && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {errors.submit}
          </div>
          {submitErrorNeedsDidOnboarding(errors.submit) && (
            <GetDidCta compact reason="missing_did" message={errors.submit} />
          )}
        </div>
      )}

      <div className="pt-4">
        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          className="w-full"
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </div>
    </form>
  );
};

export default SportsAgentForm;